import { type ReactNode, useMemo } from 'react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { stripCitationMarkers } from '../lib/citationMarkers'
import type { ResponseHighlightProps } from './ResponseHighlight.types'

type Highlighter = (text: string) => ReactNode

// Does the brand name appear LITERALLY in this text (whole word, case-insensitive)?
// This is the exact condition under which ResponseHighlight produces a brand <mark>
// — so callers can gate a "jump to mention" control on it and be guaranteed there's
// an anchor to jump to. Note this is stricter than the analyzer's mention verdict,
// which also counts INDIRECT mentions (brand implied but never named, e.g. "Steam"
// implying Valve) that have no literal text to scroll to.
export function textNamesBrand(text: string, brand: string): boolean {
  if (!text || !brand || brand.length < 2) return false

  const escaped = brand.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

  return new RegExp(`\\b${escaped}\\b`, 'i').test(text)
}

// Splits raw text on brand/competitor names and wraps matches in <mark>.
// Returned as a function so it can run on every text node react-markdown emits,
// letting highlighting survive inside rendered markdown (headings, list items,
// table cells, paragraphs) rather than only on a flat string.
function makeHighlighter(brand: string, competitors: string[]): Highlighter {
  const all = [
    { name: brand, kind: 'brand' as const },
    ...competitors.map((c) => ({ name: c, kind: 'comp' as const })),
  ]
    .filter((a) => a.name && a.name.length > 1)
    .sort((a, b) => b.name.length - a.name.length)

  if (all.length === 0) return (text) => text

  const escaped = all.map((a) => a.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
  // \b word boundaries so a short brand like "On" only matches as a whole word,
  // not the "on" inside "running"/"cushion". Boundaries hug the alternation as a
  // group; \b is zero-width so it doesn't consume the surrounding characters.
  const re = new RegExp(`\\b(${escaped.join('|')})\\b`, 'gi')

  return (text) => {
    const out: ReactNode[] = []
    let last = 0
    re.lastIndex = 0
    let next = re.exec(text)
    while (next !== null) {
      const match = next
      if (match.index > last) out.push(text.slice(last, match.index))
      const matched = all.find(
        (a) => a.name.toLowerCase() === match[0].toLowerCase()
      )
      const key = `${match.index}-${match[0]}`
      if (matched?.kind === 'brand') {
        out.push(
          <mark
            key={key}
            data-brand="true"
            className="bg-primary/25 text-primary-foreground rounded px-0.5 font-semibold scroll-mt-24"
          >
            {match[0]}
          </mark>
        )
      } else if (matched?.kind === 'comp') {
        out.push(
          <mark
            key={key}
            className="bg-accent/20 text-foreground rounded px-0.5"
          >
            {match[0]}
          </mark>
        )
      } else {
        out.push(match[0])
      }
      last = match.index + match[0].length
      next = re.exec(text)
    }
    if (last < text.length) out.push(text.slice(last))
    return out.length === 1 ? out[0] : out
  }
}

// Walks react-markdown's rendered children and runs the highlighter over any
// plain-string leaves, leaving element nodes (bold, links, etc.) untouched.
function highlightChildren(
  children: ReactNode,
  highlight: Highlighter
): ReactNode {
  if (typeof children === 'string') return highlight(children)
  if (Array.isArray(children))
    return children.map((c, i) => (
      <span key={i}>{highlightChildren(c, highlight)}</span>
    ))
  return children
}

export function ResponseHighlight({
  text,
  brand,
  competitors,
}: ResponseHighlightProps) {
  // Stable key over the highlight inputs so the memo below does not churn when
  // the parent passes a fresh `competitors` array with the same contents.
  const competitorsKey = competitors.join('\u0000')

  // Rebuild the highlighter (and its compiled regex) only when the terms change.
  // biome-ignore lint/correctness/useExhaustiveDependencies: competitorsKey is a stable stand-in for the competitors array
  const highlight = useMemo(
    () => makeHighlighter(brand, competitors),
    [brand, competitorsKey]
  )

  const body = useMemo(() => stripCitationMarkers(text), [text])

  if (!text) return null

  return (
    <div className="text-sm text-foreground/85 leading-relaxed space-y-3 [&_table]:w-full [&_table]:border-collapse [&_table]:text-xs [&_th]:border [&_th]:border-border [&_th]:bg-secondary/40 [&_th]:px-2 [&_th]:py-1.5 [&_th]:text-left [&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1.5 [&_h1]:text-base [&_h1]:font-semibold [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:mt-4 [&_h3]:text-sm [&_h3]:font-semibold [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:space-y-1 [&_strong]:font-semibold [&_strong]:text-foreground [&_a]:text-primary [&_a]:underline [&_code]:bg-secondary/50 [&_code]:rounded [&_code]:px-1 [&_code]:py-0.5 [&_code]:[&_code]:text-xs [&_table]:overflow-hidden [&_table]:rounded-md">
      <Markdown
        remarkPlugins={[remarkGfm]}
        components={{
          // An inline citation points off-site; keep the run open behind it.
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noopener noreferrer">
              {children}
            </a>
          ),
          p: ({ children }) => <p>{highlightChildren(children, highlight)}</p>,
          li: ({ children }) => (
            <li>{highlightChildren(children, highlight)}</li>
          ),
          td: ({ children }) => (
            <td>{highlightChildren(children, highlight)}</td>
          ),
          th: ({ children }) => (
            <th>{highlightChildren(children, highlight)}</th>
          ),
          h1: ({ children }) => (
            <h1>{highlightChildren(children, highlight)}</h1>
          ),
          h2: ({ children }) => (
            <h2>{highlightChildren(children, highlight)}</h2>
          ),
          h3: ({ children }) => (
            <h3>{highlightChildren(children, highlight)}</h3>
          ),
        }}
      >
        {body}
      </Markdown>
    </div>
  )
}
